import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Layout from '../layouts/application';
import { ThemeProvider } from '@mui/material/styles';
import { themeAttuned } from '../../constants/theme';
import { ConfirmDialogProvider } from '../../components/modals/confirm_dialog';
import { LuzmoDashboardComponent } from '@luzmo/react-embed';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import SurveyCollectionsNav from '../../components/survey_collections/nav';

const Container = (props) => {
  const { links, permission_props, luzmoAuthKey = '', luzmoAuthToken = '', luzmoDashboards = [], back_url } = props;
  const [selectedDashboardId, setSelectedDashboardId] = useState(luzmoDashboards[0]?.id || '');
  const luzmoRef = useRef(null);

  const getDashboardName = (dashboard) => {
    if (typeof dashboard.name === 'string') {
      return dashboard.name;
    }

    if (typeof dashboard.name === 'object' && dashboard.name) {
      return dashboard.name.en || dashboard.name.default || Object.values(dashboard.name)[0] || dashboard.id;
    }

    return dashboard.id || 'Unnamed Dashboard';
  };

  useEffect(() => {
    if (luzmoRef.current && selectedDashboardId) {
      luzmoRef.current.dashboardId = selectedDashboardId;
    }
  }, [selectedDashboardId]);

  const setLuzmoRef = (node) => {
    if (!node) return;

    luzmoRef.current = node;
  };

  const handleDashboardChange = (event) => {
    setSelectedDashboardId(event.target.value);
  };

  return (
    <>
      <SurveyCollectionsNav links={links} permissionProps={permission_props} activeTab="dashboards" backUrl={back_url || links.data_analysis} />

      {luzmoDashboards.length > 1 && (
        <Box sx={{ p: 2 }}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ maxWidth: 400 }}>
            <InputLabel id="dashboard-select-label">Select Dashboard</InputLabel>
            <Select
              labelId="dashboard-select-label"
              id="dashboard-select"
              value={selectedDashboardId}
              label="Select Dashboard"
              onChange={handleDashboardChange}
            >
              {luzmoDashboards.map((dashboard) => (
                <MenuItem key={dashboard.id} value={dashboard.id}>
                  {getDashboardName(dashboard)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {luzmoDashboards.length === 1 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" component="h2">
            {getDashboardName(luzmoDashboards[0])}
          </Typography>
        </Box>
      )}

      {luzmoDashboards.length > 0 && (
        <Box
          sx={{
            backgroundColor: '#eef3f6',
            minHeight: '600px',
            padding: 2,
            margin: 2,
            borderRadius: 1,
            '& > div': {
              height: '100%',
              minHeight: '560px',
            },
            '& iframe': {
              backgroundColor: 'transparent !important',
              borderRadius: '4px',
            },
            '& .luzmo-embed-container': {
              backgroundColor: 'transparent !important',
            },
          }}
        >
          <LuzmoDashboardComponent
            ref={setLuzmoRef}
            appServer="https://app.us.luzmo.com/"
            apiHost="https://api.us.luzmo.com/"
            authKey={luzmoAuthKey}
            authToken={luzmoAuthToken}
            loaderBackground="#eef3f6"
            loaderSpinnerColor="rgb(103,187,185)"
            loaderSpinnerBackground="#eef3f6"
          />
        </Box>
      )}

      {luzmoDashboards.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">No dashboards available for your account.</Typography>
        </Box>
      )}
    </>
  );
};

Container.propTypes = {
  links: PropTypes.object.isRequired,
  permission_props: PropTypes.object,
  luzmoAuthKey: PropTypes.string,
  luzmoAuthToken: PropTypes.string,
  luzmoDashboards: PropTypes.array,
  back_url: PropTypes.string,
};

export default (props, railsContext) => {
  return () => (
    <Layout railsContext={railsContext}>
      <ThemeProvider theme={themeAttuned}>
        <ConfirmDialogProvider>
          <Container {...props} />
        </ConfirmDialogProvider>
      </ThemeProvider>
    </Layout>
  );
};
